import type {ReactNode} from 'react'
import {Form} from 'react-router'
import {Button} from '@ui/Button/button'
import {Icon} from '@ui/Icon/icon'
import {track} from '@core/utils/analytics'
import {FacebookSignIn} from '@core/auth-ui/FacebookSignIn'
import {MagicLinkForm, useMagicLinkSent} from '@core/auth-ui/MagicLinkForm/MagicLinkForm'
import {DEFAULT_SIGN_IN_COPY, type SignInCopy} from '@core/auth-ui/signInCopy'
import styles from './SignInOptions.module.scss'

type SignInOptionsProps = {
    googleAuthAction: string
    autoFocus?: boolean
    /** Same-origin relative path to return to once auth completes (e.g. the pack page that
     *  triggered a gate). Carried through both the Google OAuth roundtrip and the magic-link
     *  email. Omitted entirely when not passed, so every existing caller keeps landing on '/'. */
    redirectTo?: string
    /** Which surface raised the gate (e.g. 'pack-checkout', 'swap', 'login-page') — mirrors
     *  gate_hit's own `trigger` field so auth_option_selected joins back to it. Threaded the same
     *  way as redirectTo: optional, defaulted below, existing callers unaffected. */
    trigger?: string
    /** Pack slug, when the gate was raised by a pack page — attributes the choice to that pack. */
    packSlug?: string
    /** Overrides the button's icon. Defaults to the single-colour `google-fill` sprite (fills with
     *  the button's own text colour via `currentColor` — narcis's white-on-white pill and every
     *  SignupGate mount keep this unchanged). A house whose gate register asks for Google's literal
     *  four-colour mark (quies_session_architecture decisions.md §31) passes `<GoogleGIcon />`
     *  instead, rather than forking this component. */
    googleIcon?: ReactNode
    /** The words of the controls; English when omitted. */
    copy?: SignInCopy
}

export function SignInOptions({googleAuthAction, autoFocus, redirectTo, trigger = 'unspecified', packSlug, googleIcon, copy = DEFAULT_SIGN_IN_COPY}: SignInOptionsProps) {
    // Once a link is on its way and the copy has a waiting view, that view stands alone and the Google
    // button returns inside it, as the way out. The tree keeps its shape so the form keeps its email.
    const sent = useMagicLinkSent(!!copy.waiting)
    const waitingView = !!copy.waiting && sent

    const googleForm = (label: string) => (
        <>
            {/* reloadDocument: full-document POST, not a SPA .data fetch — Safari content blockers
                kill google-tracker-looking fetch URLs, and the OAuth bounce leaves the site anyway.
                track() must run synchronously here with nothing awaited/returned — a beacon queued
                ahead of the unload, not a promise the reloadDocument navigation would outrun. */}
            <Form
                action={googleAuthAction}
                method="POST"
                className={styles.googleForm}
                reloadDocument
                onSubmit={() => track('auth_option_selected', {method: 'google', trigger, packSlug})}
            >
                {redirectTo && <input type="hidden" name="redirectTo" value={redirectTo} />}
                {/* Carried through the OAuth roundtrip the same way redirectTo is (auth.server.ts's
                    setAuthReturn stashes both) — the post-callback sign_up event (funnel spec
                    §2) reads it back off the redirect URL. */}
                <input type="hidden" name="trigger" value={trigger} />
                <Button type="submit" variant="secondary" size="large" className={styles.googleButton}>
                    {googleIcon ?? <Icon id="google-fill" size={18} />}
                    {label}
                </Button>
            </Form>
        </>
    )

    return (
        <>
            <div hidden={waitingView} className={styles.entry}>
                {googleForm(copy.google)}
                <FacebookSignIn
                    formClassName={styles.googleForm}
                    redirectTo={redirectTo}
                    trigger={trigger}
                    packSlug={packSlug}
                    renderButton={content => (
                        <Button type="submit" variant="secondary" size="large" className={styles.googleButton}>
                            {content}
                        </Button>
                    )}
                />
                <div className={styles.separator}><span>{copy.or}</span></div>
            </div>

            <MagicLinkForm
                autoFocus={autoFocus}
                redirectTo={redirectTo}
                trigger={trigger}
                packSlug={packSlug}
                copy={copy}
                alternative={copy.waiting ? googleForm(copy.waiting.googleInstead) : undefined}
            />
        </>
    )
}
